/**
 * FormRenderer Component - Renders forms from Formr schemas
 * 
 * CHANGE HISTORY:
 * - 251102_1146 [pending] danielbeer: Moved from Formr to Interfacr
 * - 251023_1954 [pending] danielbeer: Initial creation
 */

import React from 'react';
import { useForm } from '../hooks/useFormr';
import type { FieldConfig } from '@opcentral/formr';

export interface FormRendererProps {
  formId: string;
  onSubmit?: (result) => void;
  onCancel?: () => void;
  className?: string;
}

export function FormRenderer({ formId, onSubmit, onCancel, className }: FormRendererProps) {
  const { form, setFieldValue, submit, isSubmitting } = useForm(formId);
  
  if (!form) {
    return <div>Form not found: {formId}</div>;
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await submit();
    if (result.success && onSubmit) {
      onSubmit(result);
    }
  };
  
  const renderField = (field: FieldConfig) => {
    // Check conditional display
    if (field.conditional) {
      const conditionField = form.data[field.conditional.field];
      const conditionMet = evaluateCondition(
        conditionField,
        field.conditional.operator,
        field.conditional.value
      );
      if (!conditionMet) {
        return null;
      }
    }
    
    if (field.hidden) {
      return null;
    }
    
    const value = form.data[field.id] ?? '';
    const error = form.errors[field.id];
    const touched = form.touched[field.id];
    
    return (
      <div key={field.id} className={`form-field ${field.className || ''}`} style={field.style}>
        <label htmlFor={field.id} className="form-label">
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>
        
        {renderInput(field, value, (val) => setFieldValue(field.id, val))}
        
        {field.helpText && (
          <div className="form-help-text">{field.helpText}</div>
        )}
        
        {touched && error && (
          <div className="form-error">{error}</div>
        )}
      </div>
    );
  };
  
  const renderInput = (field: FieldConfig, value, onChange: (val) => void) => {
    const commonProps = {
      id: field.id,
      name: field.id,
      disabled: field.disabled || isSubmitting,
      placeholder: field.placeholder,
      required: field.required,
      className: 'form-input'
    };
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
          />
        );
      
      case 'select':
        return (
          <select
            {...commonProps}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select...</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      
      case 'multiselect':
        return (
          <select
            {...commonProps}
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, opt => opt.value);
              onChange(selected);
            }}
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      
      case 'radio':
        return (
          <div className="form-radio-group">
            {field.options?.map((opt) => (
              <label key={opt.value} className="form-radio-label">
                <input
                  type="radio"
                  name={field.id}
                  value={opt.value}
                  checked={value === opt.value}
                  disabled={opt.disabled || field.disabled || isSubmitting}
                  onChange={(e) => onChange(e.target.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        if (field.options) {
          // Multiple checkboxes
          const values = Array.isArray(value) ? value : [];
          return (
            <div className="form-checkbox-group">
              {field.options.map((opt) => (
                <label key={opt.value} className="form-checkbox-label">
                  <input
                    type="checkbox"
                    value={opt.value}
                    checked={values.includes(opt.value)}
                    disabled={opt.disabled || field.disabled || isSubmitting}
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...values, opt.value]
                        : values.filter(v => v !== opt.value);
                      onChange(newValues);
                    }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          );
        } else {
          // Single checkbox
          return (
            <label className="form-checkbox-label">
              <input
                type="checkbox"
                checked={!!value}
                disabled={field.disabled || isSubmitting}
                onChange={(e) => onChange(e.target.checked)}
              />
              <span>{field.label}</span>
            </label>
          );
        }
      
      case 'file':
        return (
          <input
            {...commonProps}
            type="file"
            onChange={(e) => onChange(e.target.files)}
          />
        );
      
      default:
        return (
          <input
            {...commonProps}
            type={field.type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };
  
  const evaluateCondition = (fieldValue, operator: string, conditionValue): boolean => {
    switch (operator) {
      case 'equals':
        return fieldValue === conditionValue;
      case 'notEquals':
        return fieldValue !== conditionValue;
      case 'contains':
        return String(fieldValue).includes(String(conditionValue));
      case 'greaterThan':
        return Number(fieldValue) > Number(conditionValue);
      case 'lessThan':
        return Number(fieldValue) < Number(conditionValue);
      default:
        return true;
    }
  };
  
  const layoutClass = form.config.layout === 'grid' ? 'form-grid' : `form-${form.config.layout || 'vertical'}`;
  const gridStyle = form.config.layout === 'grid' && form.config.columns
    ? { gridTemplateColumns: `repeat(${form.config.columns}, 1fr)` }
    : undefined;
  
  return (
    <form onSubmit={handleSubmit} className={`formr-form ${layoutClass} ${className || ''}`}>
      {form.config.title && (
        <h2 className="form-title">{form.config.title}</h2>
      )}
      
      {form.config.description && (
        <p className="form-description">{form.config.description}</p>
      )}
      
      <div className="form-fields" style={gridStyle}>
        {form.config.fields.map(renderField)}
      </div>
      
      <div className="form-actions">
        {form.config.submitButton && (
          <button
            type="submit"
            disabled={isSubmitting || !form.isValid}
            className={form.config.submitButton.styleClass || 'btn-primary'}
          >
            {form.config.submitButton.icon && <span>{form.config.submitButton.icon}</span>}
            {form.config.submitButton.label}
          </button>
        )}
        
        {form.config.cancelButton && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={form.config.cancelButton.styleClass || 'btn-secondary'}
          >
            {form.config.cancelButton.icon && <span>{form.config.cancelButton.icon}</span>}
            {form.config.cancelButton.label}
          </button>
        )}
      </div>
    </form>
  );
}
